import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import DashboardLayout from "./components/Layout";
import useRole, { ROLES, EVENT_VIEWER, roleLabel } from "./components/useRole";
import { createEventUser, deleteEventUser, getEventUserCandidates, getEventUsers, updateEventUser } from "../../api/user/viewers";

const roleBadge = {
  "event-admin": "bg-purple-50 text-purple-700",
  "event-viewer": "bg-emerald-50 text-emerald-700",
  "ticket-control": "bg-amber-50 text-amber-700",
};

const emptyForm = { name: "", email: "", password: "", role: EVENT_VIEWER, existingId: "" };

// Who may work on this event, and with which role
const UsersPage = () => {
  const { eventId } = useParams();
  const { isAdmin } = useRole(eventId);
  const [users, setUsers] = useState([]);
  // accounts from this admin's other events, so a known person can be reused
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  // "existing": give a known account a role here; "new": make an account.
  // Unset until the admin picks a side, so the default can follow what there is.
  const [mode, setMode] = useState(null);
  const [search, setSearch] = useState("");
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // the row being edited, with the values typed so far
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [list, known] = await Promise.all([
      getEventUsers(eventId),
      getEventUserCandidates(eventId).catch(() => null),
    ]);
    setUsers(list?.data?.items || []);
    setCandidates(known?.data?.items || []);
  }, [eventId]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to={`/dashboard/${eventId}`} replace />;

  const picked = candidates.find((item) => item._id === form.existingId);
  // with people to choose from, start on the picker; otherwise on the form
  const isPicking = (mode || (candidates.length ? "existing" : "new")) === "existing";

  const pickExisting = (person) => {
    setStatus(null);
    setPickerOpen(false);
    setSearch("");
    setForm(person
      ? { ...form, existingId: person._id, name: person.name || "", email: person.email, password: "" }
      : { ...emptyForm, role: form.role });
  };

  // name or email, however the admin remembers them
  const query = search.trim().toLowerCase();
  const matches = candidates.filter((item) => !query
    || `${item.name || ""} ${item.email}`.toLowerCase().includes(query));

  const switchMode = (next) => {
    setMode(next);
    setStatus(null);
    setSearch("");
    setPickerOpen(false);
    setForm({ ...emptyForm, role: form.role });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatus(null);
    try {
      const res = await createEventUser(eventId, {
        name: form.name,
        email: form.email,
        role: form.role,
        // a known account keeps its own password
        ...(picked ? {} : { password: form.password }),
      });
      setForm(emptyForm);
      setStatus({
        type: "success",
        message: res?.data?.message || "İstifadəçi əlavə edildi.",
      });
      await load();
    } catch (error) {
      setStatus({ type: "error", message: error?.response?.data?.message || "Əlavə etmək olmadı." });
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (user) => {
    setStatus(null);
    setEditing({
      id: user._id, name: user.name || "", role: user.role, password: "",
      eventCount: user.eventCount, isShared: user.isShared,
    });
  };

  const onSaveEdit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatus(null);
    try {
      await updateEventUser(eventId, editing.id, {
        // an account shared with other events keeps its own name and password
        ...(editing.isShared ? {} : { name: editing.name }),
        role: editing.role,
        ...(editing.password ? { password: editing.password } : {}),
      });
      setEditing(null);
      setStatus({ type: "success", message: "İstifadəçi yeniləndi." });
      await load();
    } catch (error) {
      setStatus({ type: "error", message: error?.response?.data?.message || "Yeniləmək olmadı." });
    } finally {
      setIsSaving(false);
    }
  };

  const onRemove = async (user) => {
    if (!window.confirm(`${user.email} bu tədbirə girişi itirəcək. Davam edilsin?`)) return;
    try {
      await deleteEventUser(eventId, user._id);
      await load();
      setStatus({ type: "success", message: "İcazə ləğv edildi." });
    } catch (error) {
      setStatus({ type: "error", message: error?.response?.data?.message || "Ləğv etmək olmadı." });
    }
  };

  const selectedRole = ROLES.find((item) => item.value === form.role);

  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-3xl mx-auto mt-6 lg:mt-10 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">👥 İstifadəçilər</h2>
          <p className="text-sm text-gray-500 mt-1">
            Hər istifadəçi seçdiyiniz rola uyğun hissələri görür. Tədbiri yaradan avtomatik admindir.
            Rol yalnız bu tədbirə aiddir — eyni email başqa tədbirdə başqa rolda ola bilər.
          </p>
        </div>

        <form onSubmit={onSubmit} className="bg-white p-6 rounded-xl shadow space-y-4">
          <h3 className="text-base font-semibold">İstifadəçi əlavə et</h3>

          {/* Somebody who already works on another of this admin's events, or a
              brand new account */}
          <div className="flex gap-2">
            {[
              { value: "existing", label: "Mövcud istifadəçi" },
              { value: "new", label: "Yeni istifadəçi" },
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => switchMode(tab.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                  (tab.value === "existing") === isPicking
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {isPicking && (
            <div>
              <label className="block text-sm font-medium text-gray-700">İstifadəçi</label>
              {candidates.length === 0 ? (
                <p className="mt-1 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded p-3">
                  Başqa tədbirlərinizdə əlavə edə biləcəyiniz istifadəçi yoxdur. "Yeni istifadəçi" ilə yaradın.
                </p>
              ) : picked ? (
                <div className="mt-1 flex items-center justify-between gap-3 border border-gray-300 rounded p-2">
                  <span className="text-sm">
                    <span className="font-semibold">{picked.name || picked.email}</span>
                    <span className="text-gray-500"> · {picked.email}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => pickExisting(null)}
                    className="text-sm text-gray-500 hover:text-gray-800"
                  >
                    Dəyiş
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={search}
                    placeholder="Ad və ya email ilə axtarın"
                    onChange={(e) => { setSearch(e.target.value); setPickerOpen(true); }}
                    onFocus={() => setPickerOpen(true)}
                    // long enough for a click on one of the options to land first
                    onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
                    className="mt-1 w-full border border-gray-300 p-2 rounded"
                  />
                  {isPickerOpen && (
                    <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                      {matches.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500">Uyğun istifadəçi tapılmadı.</p>
                      ) : matches.map((person) => (
                        <button
                          key={person._id}
                          type="button"
                          onClick={() => pickExisting(person)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 border-gray-100"
                        >
                          <span className="block text-sm font-medium text-gray-900">{person.name || person.email}</span>
                          <span className="block text-xs text-gray-500">
                            {person.email}
                            {person.roles?.length > 0 && ` · ${person.roles.map(roleLabel).join(", ")}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Bu şəxs öz mövcud şifrəsi ilə daxil olur; ona yalnız bu tədbir üçün rol verilir.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Rol</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1 w-full border border-gray-300 p-2 rounded bg-white"
            >
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            {selectedRole && <p className="text-xs text-gray-500 mt-1">{selectedRole.description}</p>}
          </div>

          {!isPicking && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ad</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full border border-gray-300 p-2 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full border border-gray-300 p-2 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Şifrə</label>
                  <input
                    type="text"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1 w-full border border-gray-300 p-2 rounded"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Şifrəni özünüz təyin edin və istifadəçiyə çatdırın; o, bu email və şifrə ilə panelə daxil olur.
                Bu email artıq Tixy-də varsa, öz mövcud şifrəsi qalır və yalnız bu tədbir üçün rol təyin edilir.
              </p>
            </>
          )}
          {status && (
            <p className={`text-sm ${status.type === "success" ? "text-emerald-700" : "text-red-600"}`}>{status.message}</p>
          )}
          <button
            type="submit"
            disabled={isSaving || (isPicking && !picked)}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400"
          >
            {isSaving ? "Əlavə edilir..." : "Əlavə et"}
          </button>
        </form>

        <div className="bg-white rounded-xl shadow overflow-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-200 text-left text-sm font-semibold text-gray-700">
                <th className="p-4">Ad</th>
                <th className="p-4">Email</th>
                <th className="p-4">Rol</th>
                <th className="p-4">Əməliyyatlar</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                editing?.id === user._id ? (
                  <tr key={user._id} className="border-t text-sm bg-gray-50">
                    <td className="p-4" colSpan={4}>
                      <form onSubmit={onSaveEdit} className="space-y-3">
                        <div className="text-sm font-semibold">{user.email}</div>
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Ad</label>
                            <input
                              value={editing.name}
                              disabled={editing.isShared}
                              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                              className="mt-1 w-full border border-gray-300 p-2 rounded disabled:bg-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Rol</label>
                            <select
                              value={editing.role}
                              disabled={user.isYou}
                              onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                              className="mt-1 w-full border border-gray-300 p-2 rounded bg-white disabled:bg-gray-100"
                            >
                              {ROLES.map((role) => (
                                <option key={role.value} value={role.value}>{role.label}</option>
                              ))}
                            </select>
                            {user.isYou && <p className="text-xs text-gray-500 mt-1">Öz rolunuzu dəyişə bilməzsiniz.</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Yeni şifrə</label>
                            <input
                              type="text"
                              minLength={6}
                              disabled={editing.isShared}
                              placeholder={editing.isShared ? "Başqa tədbirlərdə də çalışır" : "Dəyişmirsinizsə, boş saxlayın"}
                              value={editing.password}
                              onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                              className="mt-1 w-full border border-gray-300 p-2 rounded disabled:bg-gray-100"
                            />
                          </div>
                        </div>
                        {editing.isShared && (
                          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                            Bu istifadəçi {editing.eventCount} tədbirdə çalışır. Dəyişiklik yalnız bu tədbirə
                            aiddir; adı və şifrəsi digər tədbirlərdə olduğu kimi qalır.
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={isSaving}
                            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400"
                          >
                            {isSaving ? "Yadda saxlanılır..." : "Yadda saxla"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                          >
                            Ləğv et
                          </button>
                        </div>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr key={user._id} className="border-t text-sm">
                    <td className="p-4">
                      {user.name || "-"}
                      {user.isYou && <span className="ml-2 text-xs text-gray-400">(siz)</span>}
                    </td>
                    <td className="p-4">{user.email}</td>
                    <td className="p-4">
                      <span className={`inline-block rounded-lg px-2 py-1 text-xs font-semibold ${roleBadge[user.role] || "bg-gray-100 text-gray-700"}`}>
                        {roleLabel(user.role)}
                      </span>
                      {user.eventCount > 1 && (
                        <span className="ml-2 text-xs text-gray-400" title="Bu rol yalnız bu tədbirə aiddir">
                          {user.eventCount} tədbir
                        </span>
                      )}
                    </td>
                    <td className="p-4 space-x-3 whitespace-nowrap">
                      <button onClick={() => startEdit(user)} className="text-blue-600 hover:underline">
                        Düzəlt
                      </button>
                      {user.isYou ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <button onClick={() => onRemove(user)} className="text-red-600 hover:underline">
                          İcazəni ləğv et
                        </button>
                      )}
                    </td>
                  </tr>
                )
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center p-4 text-gray-500">Hələ istifadəçi yoxdur.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UsersPage;
